import { Injectable } from '@angular/core';

import { environment } from '@env/environment';

// Google's recommended loading pattern: the script is fetched with
// loading=async and invokes a named global callback once every requested
// library is ready. That is later than the script's own load event, so the
// callback is what resolves the promise, not the load event. (Angular's JSONP
// backend removes its callback on the load event, which is why it cannot be
// used here.)
const callbackName = 'onGoogleMapsApiLoaded';

const apiUrl = (): string =>
  'https://maps.googleapis.com/maps/api/js' +
  '?libraries=geometry,marker' +
  '&loading=async' +
  `&callback=${callbackName}` +
  `&key=${environment.googleAPIKey}`;

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private loading: Promise<boolean> | null = null;

  /** Loads the Maps API once; every caller shares the same load. */
  public isApiLoaded(): Promise<boolean> {
    this.loading ??= this.load();
    return this.loading;
  }

  private load(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const script = document.createElement('script');
      const callbacks = window as unknown as Record<string, unknown>;

      callbacks[callbackName] = () => {
        delete callbacks[callbackName];
        resolve(true);
      };
      script.onerror = () => {
        delete callbacks[callbackName];
        resolve(false);
      };

      script.src = apiUrl();
      script.async = true;
      document.head.appendChild(script);
    });
  }
}
